<template>
  <div>
    <page-header :header="$t('drivers.schedule')" />
    <b-card id="drivers-schedule" class="mt-2">
      <template #header>
        <b-form inline>
          <b-form-group class="mr-2">
            <field
              v-if="showCompanyFilter"
              :model="where"
              :field="{
                ajax: true,
                attribute: 'company_id',
                component: 'app-relation-field',
                placeholder: $t('company.select_company'),
                source: {
                  url: 'companies',
                  params: {
                    context: 'options',
                  },
                },
              }"
              layout="inline"
              :label="$t('drivers.company')"
              class="mr-2 mb-0"
            />
          </b-form-group>
          <field
            :model="where"
            :field="{
              attribute: 'date',
              component: 'datetime-field',
              options: {
                autoClose: true,
                formatted: 'll',
                onlyDate: true,
                outputFormat: 'YYYY-MM-DD',
              },
            }"
            layout="inline"
          />
        </b-form>
      </template>

      <div v-if="loading" class="text-center text-muted">
        <i class="spinner icon-spinner4 icon-2x">
          <span class="sr-only">{{ $t('general.loading') }}</span>
        </i>
      </div>
      <div v-else-if="isEmpty" class="text-center">
        <p class="text-muted">{{ $t('general.no_data') }}</p>
      </div>
      <div v-show="!loading && !isEmpty">
        <div ref="schedule" style="height: 100%"></div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { ADMINISTER } from '../constants/permissions'
import { useVisTimeline } from '../composables/useVisTimeline'
import {
  bookingToVisItem,
  visGroupTemplate,
  visItemTemplate,
  visItemTooltip,
} from './utils'

import '../assets/scss/schedule-vis-timeline.scss'

export default {
  name: 'DriversSchedule',

  data() {
    return {
      loading: false,
      isEmpty: false,
      chart: null,
      where: {
        company_id: null,
        date: this.$moment().format('YYYY-MM-DD'),
      },
    }
  },

  computed: {
    showCompanyFilter() {
      return this.$allows(ADMINISTER)
    },
  },

  watch: {
    where: {
      handler() {
        this.updateChart()
      },
      deep: true,
    },
  },

  mounted() {
    this.updateChart()
  },

  beforeDestroy() {
    this.destroyChart()
  },

  methods: {
    drawChart(bookings, drivers) {
      if (this.isEmpty) {
        return
      }

      const options = {
        start: this.$moment(this.where.date).startOf('day').toDate(),
        end: this.$moment(this.where.date).endOf('day').toDate(),
        template: visItemTemplate,
        groupTemplate: visGroupTemplate,
        minHeight: 250,
        tooltip: {
          delay: 100,
          followMouse: false,
          overflowMethod: 'cap',
          template: (item) => {
            return visItemTooltip(item, this.$t.bind(this))
          },
        },
      }

      const groups = drivers.map((driver) => ({
        id: driver.id,
        content: driver.name,
        vehicleModel: driver.vehicle_model || this.$t('general.not_specified'),
        vehicleNumber:
          driver.vehicle_number || this.$t('general.not_specified'),
      }))
      const items = bookings.map(bookingToVisItem)

      if (this.chart) {
        this.chart.setData({
          items,
          groups,
        })
        this.chart.setOptions(options)
        return
      }

      this.$nextTick(() => {
        this.chart = useVisTimeline(this.$refs.schedule, items, groups, options)
        this.chart.on('click', this.handleClick)
      })
    },

    loadDriversData() {
      this.loading = true
      const config = {
        params: {
          where: { ...this.where },
        },
      }
      return this.$http
        .get('drivers/schedule', config)
        .then((response) => response.data)
        .finally(() => {
          this.loading = false
        })
    },

    handleClick(properties) {
      if (properties.what === 'item') {
        const booking = this.chart.itemsData.get(properties.item)
        this.$router.push({
          name: 'bookings.edit',
          params: { id: booking.id },
        })
        return
      }

      if (properties.what === 'group-label') {
        const driver = this.chart.groupsData.get(properties.group)
        this.$router.push({
          name: 'drivers.edit',
          params: { id: driver.id },
        })
        return
      }
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },

    updateChart() {
      this.loadDriversData()
        .then((driversData) => {
          this.isEmpty = driversData.data.length === 0
          this.drawChart(driversData.data, driversData.drivers)
        })
        .catch((error) => {
          if (error.message) {
            this.$toast.error(error.message)
          } else {
            this.$toast.error(this.$t('general.error'))
          }
        })
    },
  },
}
</script>
